import React, { useContext } from 'react';
import { useQueryParams } from 'hooks/custom/useQueryParams';
import GlobalContext from 'hooks/contexts/GlobalContext';
import { QUERY_PARAMS } from 'consts';
import { TMS_EVENT_TYPES as EventTypes } from 'utils/analytics/EVENT_TYPES';
import { logTmsButtonClick } from 'utils/analytics/logTmsEvent';
import ApplicationLink from './ApplicationLink';

const { PARENT_CAT_GUID_PARAM, CLICK_THROUGH_LOCATION_PARAM } = QUERY_PARAMS;

interface CtaButtonProps {
  buttonText: string;
  link: string;
  buttonActionType: string;
  trackingLabel?: EventTypes;
}

function CtaButton(props: CtaButtonProps): React.JSX.Element {
  const { buttonText, buttonActionType, link, trackingLabel = null } = props;

  let linkParams = '';
  const query = useQueryParams();

  const {
    userData: { parentCategorySelection, applicationLink },
    actions,
  } = useContext(GlobalContext);

  if (parentCategorySelection) {
    linkParams = `?${PARENT_CAT_GUID_PARAM}=${parentCategorySelection?.value}`;
    if (trackingLabel) {
      linkParams += `&${CLICK_THROUGH_LOCATION_PARAM}=${trackingLabel}`;
    }
  } else if (trackingLabel) {
    linkParams = `?${CLICK_THROUGH_LOCATION_PARAM}=${trackingLabel}`;
  }

  function handleButtonClickTracking(): void {
    logTmsButtonClick(buttonText);
  }

  function triggerClickPortalWizardModal(): void {
    actions.setActiveModalId('clickPortalFormWizardModal');
    handleButtonClickTracking();
  }

  if (buttonActionType === 'clickPortalWizard') {
    return (
      <button
        type="button"
        className="ctaButton"
        data-testid={`ctaButton--${buttonActionType}`}
        onClick={triggerClickPortalWizardModal}
      >
        {buttonText}
      </button>
    );
  }

  if (buttonActionType === 'fullPageForm') {
    const urlPath = query?.degree || 'default';

    return (
      <a
        className="ctaButton"
        data-testid={`ctaButton--${buttonActionType}`}
        href={`/${urlPath}/landing/form${linkParams}`}
        onClick={handleButtonClickTracking}
        onAuxClick={handleButtonClickTracking}
        onContextMenu={handleButtonClickTracking}
      >
        {buttonText}
      </a>
    );
  }

  if (buttonActionType === 'applicationLink') {
    return (
      <ApplicationLink
        url={applicationLink}
        buttonText={buttonText}
        onClick={handleButtonClickTracking}
      />
    );
  }

  return (
    // eslint-disable-next-line react/jsx-no-target-blank
    <a
      className="ctaButton"
      data-track-click={trackingLabel}
      data-testid={`ctaButton--${buttonActionType}`}
      href={link}
      target={buttonActionType === 'customLink' ? '_blank' : undefined}
      rel={
        buttonActionType === 'customLink' ? 'noreferrer noopener' : undefined
      }
      onClick={handleButtonClickTracking}
      onAuxClick={handleButtonClickTracking}
      onContextMenu={handleButtonClickTracking}
    >
      {buttonText}
    </a>
  );
}

export default CtaButton;
